import { api } from '../baseApi';
import { workspaceTypeChecks } from '../helpers';
import { makeRequest } from '../makeRequest';
import type {
  CreateWorkspaceRequest,
  CreateWorkspaceResponse,
  DeleteWorkspaceRequest,
  DeleteWorkspaceResponse,
  DuplicateWorkspaceRequest,
  DuplicateWorkspaceResponse,
  ExpansionWorkspaceMetaDataRequest,
  ExpansionWorkspaceMetaDataResponse,
  GetDependenciesRequest,
  GetDependenciesResponse,
  GetSessionWorkSpaceRawResponse,
  GetSessionWorkSpaceRequest,
  GetSessionWorkSpaceResponse,
  GetWorkspaceContextRequest,
  GetWorkspaceContextResponse,
  GetWorkspaceNameCheckRequest,
  GetWorkspaceNameCheckResponse,
  GetWorkspaceRequest,
  GetWorkspacesRequest,
  GetWorkspacesResponse,
  GetWorkspaceTypesResponse,
  RenameWorkspaceRequest,
  SaveWorkspaceRequest,
  SaveWorkspaceResponse,
  WorkspacePayload,
  WorkspaceTypeID,
} from './dto';
import { WorkspaceAPIRoutes } from './route';

export async function createNewWorkspace(body: CreateWorkspaceRequest): Promise<WorkspacePayload> {
  const payload = {
    ...body,
    version: __BUILD_TAG__,
  };

  if (workspaceTypeChecks.isV7Workspace(body.typeID)) {
    const res = await makeRequest(api.post<WorkspacePayload>(WorkspaceAPIRoutes.createWorkspaceDTC(), payload));

    return res;
  }

  const res = await makeRequest(api.post<CreateWorkspaceResponse>(WorkspaceAPIRoutes.createWorkspace(), payload)); //TODO: Remove in future and remove the DTC condition when migration is done

  return res.data;
}

export function duplicateWorkspace(
  params: DuplicateWorkspaceRequest,
  workspaceTypeId?: WorkspaceTypeID,
): Promise<DuplicateWorkspaceResponse> {
  if (workspaceTypeChecks.isV7Workspace(workspaceTypeId)) {
    return makeRequest(api.post(WorkspaceAPIRoutes.duplicateWorkspacev7(), params));
  }
  return makeRequest(
    api.get(WorkspaceAPIRoutes.duplicateWorkspace(), {
      params,
    }),
  );
}

export function saveWorkspace(
  body: SaveWorkspaceRequest,
  workspaceTypeId?: WorkspaceTypeID,
): Promise<SaveWorkspaceResponse> {
  if (workspaceTypeChecks.isV7Workspace(workspaceTypeId)) {
    return makeRequest(api.post(WorkspaceAPIRoutes.saveWorkspaceV7(), body));
  }
  return makeRequest(api.post(WorkspaceAPIRoutes.saveWorkspace(), body));
}
export function renameWorkspace(body: RenameWorkspaceRequest, workspaceTypeId?: WorkspaceTypeID): Promise<void> {
  if (workspaceTypeChecks.isV7Workspace(workspaceTypeId)) {
    return makeRequest(api.put(WorkspaceAPIRoutes.renameWorkspaceV7(), body));
  }
  return makeRequest(api.post(WorkspaceAPIRoutes.renameWorkspace(), body));
}

export function deleteWorkspace({
  workspaceTypeId,
  ...body
}: DeleteWorkspaceRequest): Promise<DeleteWorkspaceResponse> {
  if (workspaceTypeChecks.isV7Workspace(workspaceTypeId)) {
    return makeRequest(api.post(WorkspaceAPIRoutes.deleteWorkspaceV7(), body));
  }
  return makeRequest(api.post(WorkspaceAPIRoutes.deleteWorkspace(), body));
}

export function getDependencies(params: GetDependenciesRequest): Promise<GetDependenciesResponse> {
  return makeRequest(
    api.get(WorkspaceAPIRoutes.getDependencies(), {
      params,
    }),
  );
}

export function getUsersWorkspaces({
  advertiserIDs,
  advertiserMapping,
  workspaceTypeID,
  paginationSortBy,
  createdBy,
  ...params
}: GetWorkspacesRequest): Promise<GetWorkspacesResponse> {
  const isV7Workspace = workspaceTypeID?.every((typeID) => workspaceTypeChecks.isV7Workspace(typeID));

  // TODO: Remove this condition when all workspaces are migrated to V7
  if (isV7Workspace) {
    return makeRequest(
      api.post<GetWorkspacesResponse>(WorkspaceAPIRoutes.getWorkspaces(), {
        ...params,
        advertiserID: advertiserIDs?.length ? advertiserIDs : undefined,
        createdBy: createdBy?.length ? createdBy : undefined,
        workspaceTypeID: JSON.stringify(workspaceTypeID),
        paginationSortBy: JSON.stringify(paginationSortBy),
      }),
    );
  }

  return makeRequest(
    api.post(WorkspaceAPIRoutes.getUsersWorkspaces(), {
      ...params,
      advertiserID: advertiserIDs?.length ? advertiserIDs : undefined,
      createdBy: createdBy?.length ? createdBy : undefined,
      workspaceTypeID: JSON.stringify(workspaceTypeID),
      advertiserMapping: JSON.stringify(advertiserMapping),
      paginationSortBy: JSON.stringify(paginationSortBy),
    }),
  );
}

// TODO: Remove double call when all workspaces are migrated to V7
export async function getSingleWorkspace(params: GetWorkspaceRequest): Promise<WorkspacePayload> {
  const data = await getUsersWorkspaces({
    ...params,
    workspaceTypeID: params.workspaceTypeID ? [params.workspaceTypeID] : undefined,
  });
  const workspace = (data.data ?? data.items).find((workspace) => workspace.workspace_id === params.workspaceID);

  if (!workspace) {
    throw new Error('Workspace not found');
  }

  return workspace;
}

export function getWorkspaceContext(params: GetWorkspaceContextRequest): Promise<GetWorkspaceContextResponse> {
  return makeRequest(
    api.get(WorkspaceAPIRoutes.getWorkspaceContext(), {
      params,
    }),
  );
}

export function getWorkspaceTypes(): Promise<GetWorkspaceTypesResponse> {
  return makeRequest(api.get(WorkspaceAPIRoutes.getWorkspaceTypes()));
}

export function getSessionWorkspace({
  platformSessionID,
}: GetSessionWorkSpaceRequest): Promise<GetSessionWorkSpaceResponse> {
  return makeRequest(api.get(WorkspaceAPIRoutes.getSessionWorkspace(platformSessionID))).then(
    (data) => (data as GetSessionWorkSpaceRawResponse).response,
  );
}

export function expansionMetaData(
  body: ExpansionWorkspaceMetaDataRequest,
): Promise<ExpansionWorkspaceMetaDataResponse> {
  return makeRequest(api.post(WorkspaceAPIRoutes.expansionMetaData(), body));
}

export async function getWorkspaceNameValidation(
  params: GetWorkspaceNameCheckRequest,
  workspaceTypeId?: WorkspaceTypeID,
): Promise<GetWorkspaceNameCheckResponse> {
  if (workspaceTypeChecks.isV7Workspace(workspaceTypeId)) {
    const res = await makeRequest<{ eventID: string; name_available: boolean }>(
      api.get(WorkspaceAPIRoutes.getWorkspaceNameValidationV7(), { params }),
    );
    return { eventID: res.eventID, workspaceNameAvailable: res.name_available };
  }
  return makeRequest(api.get(WorkspaceAPIRoutes.getWorkspaceNameValidation(), { params }));
}
